import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  ModuleCard,
  GsdLoginPage,
  GsdAccountRequestPage,
  useI18n,
  LoginCard,
  DojoOsModulePlaceholder,
  GsdReadableBlock,
  findRole,
} from "@/coquille-bridge";
import { KarateFormationCockpitPage } from "@/formation/KarateFormationCockpitPage.jsx";
import { useAuth } from "@/context/AuthContext.jsx";
import { DEMO_USER_REGISTRY, getDefaultRouteForRole, getDemoContextForAccount } from "@/data/dojoOsOrgModel.js";
import { getStoredBackendUser } from "@/api/karateBackendApi.js";
import { isBackendConfigured, isHybridDataMode } from "@/config/backendStaging049.js";
import { KarateAccountRequestForm } from "@/components/KarateAccountRequestForm.jsx";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import { useHybridDataStore } from "@/hooks/useHybridDataStore.js";
import { useRoleHybridContext } from "@/hooks/useRoleHybridContext.js";
import { DemoBadge } from "@/components/DemoBadge.jsx";
import { PageSection } from "@/components/AppLayout.jsx";
import { karateAppConfig } from "@/config/karateAppConfig.js";
import { getHelpTopicRegistry } from "@/content/helpContent.js";
import { getKarateUi } from "@/i18n/karateUiI18n.js";
import { BackendZoneBadge } from "@/components/BackendZoneBadge.jsx";
import { KARATE_FULL_LOGO_ASSET } from "@/components/KarateHeaderLogo.jsx";
import { MemberTodayExtras } from "@/pages/membership/MemberPortalMembershipPages.jsx";

function useStudentContext() {
  const hybrid = useRoleHybridContext();
  const { store } = useHybridDataStore();
  const sensei = hybrid.dojo?.sensei_id ? store.instructeurs.find((i) => i.id === hybrid.dojo.sensei_id) : null;
  return {
    ctx: hybrid.demoCtx,
    dojo: hybrid.dojo,
    sensei,
    store,
    students: hybrid.students,
    ownStudent: hybrid.ownStudent,
    fromBackend: hybrid.fromBackend,
    hasDojo: hybrid.hasDojo,
    hasProfile: hybrid.hasProfile,
  };
}

export function LoginPage() {
  const { loginWithCredentials, loginError, isAuthenticated, activeRoleUi } = useAuth();
  const navigate = useNavigate();
  const { lang } = useI18n();
  const { ui, isEn } = useKarateUi();
  const authLabels = ui.accountMenu;
  const locale = isEn ? "en" : "fr";
  const [loading, setLoading] = useState(false);

  // BT-GSD-KARATE-ONBOARDING-NAV-001 — chaque nouvelle session commence sur /accueil (jamais returnTo / lastRoute).
  if (isAuthenticated) {
    return <Navigate to={getDefaultRouteForRole(activeRoleUi)} replace />;
  }

  const handleSubmit = async ({ identifier, secret }) => {
    setLoading(true);
    const result = await loginWithCredentials(identifier, secret);
    setLoading(false);
    if (result?.ok) {
      if (result.mustChangePassword) {
        navigate("/changer-mot-de-passe", { replace: true });
        return;
      }
      const key = String(identifier).trim().toLowerCase();
      const demoUser = DEMO_USER_REGISTRY[key];
      const backendUser = getStoredBackendUser();
      const role = backendUser?.role || demoUser?.role || "student";
      navigate(getDefaultRouteForRole(role === "hanshi" || role === "admin" ? "hanshi" : role), { replace: true });
    }
  };

  // Titre unique dans la carte (évite double header shell+card). Mot de passe oublié non branché → pas de faux lien.
  return (
    <GsdLoginPage
      loginCard={
        <LoginCard
          logoAsset={KARATE_FULL_LOGO_ASSET}
          title={locale === "en" ? "Log in" : "Connexion"}
          subtitle={
            locale === "en"
              ? "Sign in to your Shotokan AAMC account."
              : "Connectez-vous à votre compte Shotokan AAMC."
          }
          backToSite={{
            to: "/",
            label: authLabels.backToSite,
            ariaLabel:
              locale === "en" ? "Back to Shotokan AAMC website" : "Retour au site Shotokan AAMC",
            testId: "login-back-to-site",
          }}
          identifierLabel={locale === "en" ? "Email" : "Courriel"}
          secretLabel={locale === "en" ? "Password" : "Mot de passe"}
          showPasswordLabel={authLabels.showPassword}
          hidePasswordLabel={authLabels.hidePassword}
          primarySubmit={{
            label: locale === "en" ? "Log in" : "Se connecter",
            loadingLabel: locale === "en" ? "Signing in…" : "Connexion…",
          }}
          secondaryLinks={[
            {
              label: locale === "en" ? "Request an account" : "Demander un compte",
              href: "/demande-compte",
              testId: "login-request-account-link",
            },
            {
              label: locale === "en" ? "Need help?" : "Besoin d'aide ?",
              href: "/a-propos",
              testId: "login-help-link",
            },
          ]}
          errorMessage={loginError}
          loading={loading}
          onSubmit={handleSubmit}
          testIdPrefix="karate-login"
        />
      }
      testId="page-connexion"
    />
  );
}

export function AccountRequestPage() {
  const { submitAccountRequest } = useAuth();
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const [submitted, setSubmitted] = useState(false);

  const roleOptions = [
    { value: "student", label: locale === "en" ? "Student" : "Étudiant" },
    { value: "parent", label: "Parent" },
    { value: "sensei", label: "Sensei" },
    { value: "assistant", label: locale === "en" ? "Sempai / assistant" : "Sempai / assistant" },
    { value: "admin", label: locale === "en" ? "Admin (to validate)" : "Admin (à valider)" },
  ];

  return (
    <GsdAccountRequestPage
      title={locale === "en" ? "Request an account" : "Demander un compte"}
      subtitle={
        locale === "en"
          ? "Your request will be stored locally [DEMO] until backend is connected."
          : "Votre demande sera enregistrée localement [DÉMO] en attendant le backend."
      }
      testId="page-demande-compte"
    >
      {submitted ? (
        <p data-testid="account-request-success">
          {locale === "en" ? "Request received — status: to validate." : "Demande reçue — statut : à valider."}{" "}
          <Link to="/connexion">{locale === "en" ? "Log in" : "Connexion"}</Link>
        </p>
      ) : (
        <KarateAccountRequestForm
          roleOptions={roleOptions}
          onSubmit={(data) => {
            submitAccountRequest(data);
            setSubmitted(true);
          }}
        />
      )}
    </GsdAccountRequestPage>
  );
}

export function MonEspacePage() {
  const { name, role, isAuthenticated } = useAuth();
  const { dojo, sensei, ownStudent, fromBackend, hasProfile } = useStudentContext();
  const { lang } = useI18n();
  const { localizeDemo, ui } = useKarateUi();
  const locale = lang === "en" ? "en" : "fr";
  if (!isAuthenticated) {
    return (
      <PageSection title={locale === "en" ? "My space" : "Mon espace"} testId="page-mon-espace">
        <p><Link to="/connexion">{locale === "en" ? "Log in" : "Connectez-vous"}</Link></p>
      </PageSection>
    );
  }
  return (
    <PageSection title={locale === "en" ? "My space" : "Mon espace"} subtitle={`${locale === "en" ? "Hello" : "Bonjour"} ${name}`} testId="page-mon-espace">
      <BackendZoneBadge variant={fromBackend ? "backendConnected" : "userPrepared"} testId="mon-espace-backend-badge" />
      {role === "student" && !hasProfile ? (
        <p className="warning-box" data-testid="student-no-profile">{ui.roleLimits.studentNoProfile}</p>
      ) : null}
      {role === "student" && hasProfile ? (
        <p className="muted" data-testid="student-own-profile">{ui.roleLimits.studentOwnProfile}</p>
      ) : null}
      <MemberTodayExtras />
      <div className="home-grid">
        <ModuleCard title={locale === "en" ? "Shortcuts" : "Raccourcis"} testId="espace-shortcuts">
          <ul className="link-list">
            <li><Link to="/mon-dojo">{locale === "en" ? "My dojo" : "Mon dojo"}</Link></li>
            <li><Link to="/mon-sensei">{locale === "en" ? "My Sensei" : "Mon Sensei"}</Link></li>
            <li><Link to="/ma-progression">{locale === "en" ? "My progress" : "Ma progression"}</Link></li>
            <li><Link to="/mes-titres">{locale === "en" ? "My titles" : "Mes titres"}</Link></li>
            <li><Link to="/mes-documents">{locale === "en" ? "My documents" : "Mes documents"}</Link></li>
            <li><Link to="/mon-calendrier">{locale === "en" ? "My calendar" : "Mon calendrier"}</Link></li>
            <li><Link to="/mon-adhesion">{locale === "en" ? "My membership" : "Mon adhésion"}</Link></li>
            <li><Link to="/mes-examens">{locale === "en" ? "My exams" : "Mes examens"}</Link></li>
            <li><Link to="/espace-black-dragon">Black Dragon</Link></li>
          </ul>
        </ModuleCard>
        <ModuleCard title={locale === "en" ? "Profile" : "Profil"} testId="espace-profil">
          <p>{locale === "en" ? "Role" : "Rôle"} : <strong>{role}</strong> <DemoBadge /></p>
          {ownStudent ? (
            <p data-testid="espace-student-name">{localizeDemo(ownStudent.prenom)} {localizeDemo(ownStudent.nom)}</p>
          ) : null}
          <p>{locale === "en" ? "Dojo" : "Dojo"} : {localizeDemo(dojo?.nom ?? "—")}</p>
          <p>{locale === "en" ? "Sensei" : "Sensei"} : {localizeDemo(sensei?.nom ?? "—")}</p>
        </ModuleCard>
      </div>
    </PageSection>
  );
}

export function MesInformationsPage() {
  const { name, role, activeRoleUi, isAuthenticated, username, phone, demoRoles } = useAuth();
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const shellRole = isAuthenticated ? activeRoleUi : "guest";
  const roleMeta = findRole(karateAppConfig, shellRole, locale);
  const profileLabels = getKarateUi(locale).accountMenu;
  const assignedRoles = demoRoles?.length
    ? demoRoles.map((r) => r.label).join(", ")
    : roleMeta?.label ?? role;

  if (!isAuthenticated) {
    return (
      <PageSection
        title={locale === "en" ? "My profile" : "Mon profil"}
        testId="page-mes-informations"
      >
        <p data-testid="mes-informations-login-required">
          {locale === "en" ? "Sign-in required." : "Connexion requise."}
        </p>
        <p>
          <Link to="/connexion">{locale === "en" ? "Log in" : "Connexion"}</Link>
        </p>
      </PageSection>
    );
  }

  return (
    <PageSection
      title={locale === "en" ? "My profile" : "Mon profil"}
      subtitle={
        locale === "en"
          ? "View and update your personal information."
          : "Consultez et mettez à jour vos renseignements personnels."
      }
      testId="page-mes-informations"
    >
      <GsdReadableBlock testId="mes-informations-panel" className="profile-panel">
        <dl className="info-dl" data-testid="mes-informations-dl">
          <dt>{locale === "en" ? "Display name" : "Nom"}</dt>
          <dd data-testid="mes-informations-name">{name}</dd>
          <dt>{locale === "en" ? "Email" : "Courriel"}</dt>
          <dd data-testid="mes-informations-email">{username || "—"}</dd>
          <dt>{locale === "en" ? "Phone" : "Téléphone"}</dt>
          <dd data-testid="mes-informations-phone">{phone || "—"}</dd>
          <dt>{locale === "en" ? "Role(s)" : "Rôle(s)"}</dt>
          <dd data-testid="mes-informations-role">{assignedRoles}</dd>
          <dt>{profileLabels.currentMode}</dt>
          <dd data-testid="mes-informations-active-mode">{roleMeta?.label ?? shellRole}</dd>
        </dl>
        <p className="muted">
          <Link to="/changer-mot-de-passe" data-testid="mes-informations-change-password-link">
            {profileLabels.changePassword}
          </Link>
        </p>
      </GsdReadableBlock>
    </PageSection>
  );
}

export function MonDojoPage() {
  const { isAuthenticated, role, username } = useAuth();
  const { dojo, sensei } = useStudentContext();
  const { lang } = useI18n();
  const { localizeDemo } = useKarateUi();
  const locale = lang === "en" ? "en" : "fr";
  if (!isAuthenticated) {
    return (
      <PageSection title={locale === "en" ? "My dojo" : "Mon dojo"} testId="page-mon-dojo">
        <p data-testid="mon-dojo-login-required">
          {locale === "en" ? "Sign-in required." : "Connexion requise."}{" "}
          <Link to="/connexion">{locale === "en" ? "Sign in" : "Connexion"}</Link>
        </p>
      </PageSection>
    );
  }
  return (
    <PageSection title={locale === "en" ? "My dojo" : "Mon dojo"} testId="page-mon-dojo">
      <GsdReadableBlock>
        <p>{localizeDemo(dojo?.nom ?? "—")} <DemoBadge /></p>
        <p>{localizeDemo(dojo?.ville)} — {localizeDemo(dojo?.adresse)}</p>
        <p>{locale === "en" ? "Sensei" : "Sensei"} : {localizeDemo(sensei?.nom ?? "—")} ({sensei?.titre ?? "—"})</p>
        <p className="muted">
          {role === "sensei"
            ? locale === "en"
              ? "Sensei view — demo dojo linked to your account."
              : "Vue Sensei — dojo démo lié à votre compte."
            : locale === "en"
              ? "Student view — confirm affiliation with your Sensei."
              : "Vue étudiant — rattachement à confirmer avec votre Sensei."}
        </p>
      </GsdReadableBlock>
    </PageSection>
  );
}

export function MesDocumentsPage() {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  return (
    <PageSection
      title={locale === "en" ? "My documents" : "Mes documents"}
      subtitle={
        locale === "en"
          ? "Visible documents for your role — demo Phase 1."
          : "Documents visibles selon votre rôle — démo Phase 1."
      }
      testId="page-mes-documents"
    >
      <GsdReadableBlock testId="student-documents-demo-notice">
        <p>
          {locale === "en"
            ? "Browse the Documents section filtered for your profile. No confidential authority files in this space."
            : "Consultez la section Documents filtrée selon votre profil. Aucun document d'autorité confidentiel dans cet espace."}{" "}
          <DemoBadge />
        </p>
        <p>
          <Link to="/documents">{locale === "en" ? "Open documents" : "Ouvrir les documents"}</Link>
        </p>
      </GsdReadableBlock>
    </PageSection>
  );
}

export function AidePage() {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const topic = getHelpTopicRegistry(locale).aide;

  return (
    <PageSection title={locale === "en" ? "Help" : "Aide"} testId="page-aide">
      <GsdReadableBlock testId="aide-page-body">
        <p data-testid="aide-page-purpose">{topic.purpose}</p>
        {topic.actions?.length ? (
          <ul data-testid="aide-page-actions">
            {topic.actions.map((action) => (
              <li key={action}>{action}</li>
            ))}
          </ul>
        ) : null}
        {topic.warning ? (
          <p className="muted" data-testid="aide-page-warning">
            {topic.warning}
          </p>
        ) : null}
        {topic.nextStep ? (
          <p data-testid="aide-page-next">{topic.nextStep}</p>
        ) : null}
      </GsdReadableBlock>
      <p className="muted" data-testid="aide-page-footer-hint">
        {locale === "en"
          ? "The header Help button remains the main way to get contextual help on each screen."
          : "Le bouton Aide en en-tête reste le flux principal pour l'aide contextualisée sur chaque écran."}
      </p>
    </PageSection>
  );
}

export function FormationPage() {
  return <KarateFormationCockpitPage />;
}

function FuturePlaceholder({ title, testId }) {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  return (
    <PageSection title={title} testId={testId}>
      <GsdReadableBlock testId={`${testId}-placeholder`}>
        <p className="muted">
          {locale === "en"
            ? "Phase 1 — demo preview only [DEMO]. Full feature planned for Phase 2+."
            : "Phase 1 — aperçu démo seulement [DÉMO]. Fonctionnalité complète prévue en Phase 2+."}
        </p>
      </GsdReadableBlock>
    </PageSection>
  );
}

export function MonSenseiPage() {
  const { sensei, dojo } = useStudentContext();
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  return (
    <PageSection title={locale === "en" ? "My Sensei" : "Mon Sensei"} testId="page-mon-sensei">
      <GsdReadableBlock>
        {sensei ? (
          <>
            <p><strong>{sensei.nom}</strong> — {sensei.titre} <DemoBadge /></p>
            <p>{sensei.courriel || "—"}</p>
            <p>{locale === "en" ? "Dojo" : "Dojo"} : {dojo?.nom}</p>
          </>
        ) : (
          <p className="muted">{locale === "en" ? "No Sensei linked yet [DEMO]." : "Aucun Sensei lié pour l'instant [DÉMO]."}</p>
        )}
      </GsdReadableBlock>
    </PageSection>
  );
}

export function SenseiDashboardPage() {
  const { dojo, students, fromBackend, hasDojo } = useStudentContext();
  const { lang } = useI18n();
  const { localizeDemo, demoBadge, ui } = useKarateUi();
  const locale = lang === "en" ? "en" : "fr";
  return (
    <PageSection
      title={locale === "en" ? "Sensei dashboard" : "Tableau de bord Sensei"}
      subtitle={locale === "en" ? "Manager space — Phase 1 demo" : "Espace gestionnaire — démo Phase 1"}
      testId="page-sensei-dashboard"
    >
      <BackendZoneBadge variant={fromBackend ? "backendConnected" : "managerPrepared"} testId="sensei-dashboard-backend-badge" />
      {!hasDojo ? (
        <p className="warning-box" data-testid="sensei-no-dojo">{ui.roleLimits.senseiNoDojo}</p>
      ) : (
        <p className="muted" data-testid="sensei-dojo-scope">{ui.roleLimits.senseiDojoScope}</p>
      )}
      <GsdReadableBlock>
        <p>{localizeDemo(dojo?.nom)} <DemoBadge /></p>
        <div className="home-grid">
          <ModuleCard title={locale === "en" ? "My dojo" : "Mon dojo"} testId="sensei-dash-dojo">
            <p><Link to="/sensei/mon-dojo">{localizeDemo(dojo?.nom)}</Link></p>
          </ModuleCard>
          <ModuleCard title={locale === "en" ? "My students" : "Mes étudiants"} testId="sensei-dash-students">
            <p>{students.length} {demoBadge}</p>
            <Link to="/sensei/etudiants">{locale === "en" ? "View list" : "Voir la liste"}</Link>
          </ModuleCard>
          <ModuleCard title={locale === "en" ? "Classes" : "Classes / cours"} testId="sensei-dash-classes">
            <Link to="/sensei/classes">{locale === "en" ? "My classes" : "Mes classes"}</Link>
          </ModuleCard>
          <ModuleCard title={locale === "en" ? "Events" : "Événements"} testId="sensei-dash-events">
            <Link to="/sensei/evenements">{locale === "en" ? "View events" : "Voir événements"}</Link>
          </ModuleCard>
          <ModuleCard title={locale === "en" ? "Attendance" : "Présences"} testId="sensei-dash-presences">
            <Link to="/sensei/presences">{locale === "en" ? "Light attendance" : "Présences légères"}</Link>
          </ModuleCard>
          <ModuleCard title={locale === "en" ? "Progress" : "Progression"} testId="sensei-dash-progress">
            <Link to="/sensei/progression">{locale === "en" ? "Student progress" : "Progression étudiants"}</Link>
          </ModuleCard>
          <ModuleCard title={locale === "en" ? "Exams" : "Examens"} testId="sensei-dash-examens">
            <Link to="/sensei/examens">{locale === "en" ? "Light exams" : "Examens légers"}</Link>
          </ModuleCard>
          <ModuleCard title="Shotokan Black Dragon" testId="sensei-dash-shotokan">
            <p>{locale === "en" ? "Titles, requests and proofs" : "Titres, demandes et preuves"}</p>
            <Link to="/sensei/shotokan-black-dragon">
              {locale === "en" ? "Open module" : "Ouvrir le module"}
            </Link>
          </ModuleCard>
        </div>
      </GsdReadableBlock>
    </PageSection>
  );
}

export function SenseiStudentsPage() {
  const { students, hasDojo } = useStudentContext();
  const { lang } = useI18n();
  const { ui } = useKarateUi();
  const locale = lang === "en" ? "en" : "fr";
  return (
    <PageSection title={locale === "en" ? "My students" : "Mes étudiants"} testId="page-sensei-etudiants">
      {!hasDojo ? <p data-testid="sensei-students-no-dojo">{ui.roleLimits.senseiNoDojo}</p> : null}
      <ul data-testid="sensei-students-list">
        {students.map((s) => (
          <li key={s.id}>{s.prenom} {s.nom} — {s.groupe} · {s.ceinture} <DemoBadge /></li>
        ))}
      </ul>
    </PageSection>
  );
}

export function SenseiDocumentsPage() {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  return (
    <PageSection
      title={locale === "en" ? "Useful documents" : "Documents utiles"}
      subtitle={locale === "en" ? "Shared with your dojo — demo Phase 1" : "Partagés avec votre dojo — démo Phase 1"}
      testId="page-sensei-documents"
    >
      <p>
        {locale === "en"
          ? "Shared documents for your dojo — see also the Documents section."
          : "Documents partagés avec votre dojo — voir aussi la section Documents."}{" "}
        <DemoBadge />
      </p>
      <p>
        <Link to="/documents">{locale === "en" ? "Open documents" : "Ouvrir les documents"}</Link>
      </p>
    </PageSection>
  );
}

export function SenseiCorrectionsPage() {
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  return (
    <FuturePlaceholder
      title={locale === "en" ? "Corrections / requests" : "Corrections / demandes"}
      testId="page-sensei-corrections"
    />
  );
}
